import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { BookableSlot } from './helpers';

interface SlotGridProps {
  selectedTutorName: string | undefined;
  selectedDate: string;
  isLoadingSlots: boolean;
  availableSlots: BookableSlot[];
  selectedSlotStart: string;
  onSelectSlotStart: (slotStart: string) => void;
}

export function SlotGrid({
  selectedTutorName,
  selectedDate,
  isLoadingSlots,
  availableSlots,
  selectedSlotStart,
  onSelectSlotStart,
}: SlotGridProps) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700 bg-white dark:bg-slate-800/10">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 block">
        4. Available Time Slots
      </p>

      {!selectedTutorName || !selectedDate ? (
        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-4 text-center dark:border-slate-800 dark:bg-slate-900/10">
          <p className="text-sm text-slate-505 dark:text-slate-400">
            {!selectedTutorName
              ? 'Please select a tutor and a date to view available time slots.'
              : 'Please select a date to view available time slots.'}
          </p>
        </div>
      ) : isLoadingSlots ? (
        <div className="py-6 text-center text-sm text-slate-550 dark:text-slate-400">
          Loading available slots…
        </div>
      ) : availableSlots.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {availableSlots.map((slot) => {
            const slotStartIso = slot.startUtc.toISOString();
            const isSelected = selectedSlotStart === slotStartIso;
            return (
              <button
                key={slotStartIso}
                type="button"
                onClick={() => onSelectSlotStart(slotStartIso)}
                className={cn(
                  'rounded-md h-9 px-1.5 sm:px-3 text-xs font-medium border transition-all cursor-pointer flex items-center justify-center whitespace-nowrap',
                  isSelected
                    ? 'bg-blue-600 border-blue-600 text-white shadow-sm dark:bg-blue-505 dark:border-blue-505'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700',
                )}
              >
                {format(slot.startUtc, 'h:mm a')} – {format(slot.endUtc, 'h:mm a')}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
          {selectedTutorName} has no available slots on this date. Try another date.
        </p>
      )}
    </div>
  );
}
